import type { Editor } from '@tiptap/core';
import { store } from '@/state/store';
import {
  rootComments, repliesOf, addComment, replyToComment,
  toggleResolved, deleteComment, commentIdsAtSelection,
  deleteAllComments, deleteResolvedComments, resolvedThreadCount, threadCount,
} from '@/editor/commentService';
import type { DocComment } from '@/state/types';

// ═══════════════════════════════════════════════════════════════
// Postranní panel komentářů (Fáze 3).
//
// Zobrazuje vlákna komentářů vedle dokumentu — jako ve Wordu. Vlákno =
// kořenový komentář + odpovědi. Podporuje přidání, odpověď, vyřešení
// a smazání.
//
// Panel se překresluje ze store (doc.comments) a zvýrazňuje vlákno, jehož
// text je právě pod kurzorem.
// ═══════════════════════════════════════════════════════════════

export class CommentsPanel {
  private root: HTMLElement;
  private editor: Editor;
  private listEl: HTMLElement;
  private unsub: () => void;
  private activeIds: number[] = [];
  private open = false;
  /** Volané při otevření/zavření panelu — ribbon podle toho zvýrazní tlačítko. */
  onVisibilityChange?: (open: boolean) => void;

  constructor(container: HTMLElement, editor: Editor) {
    this.editor = editor;

    this.root = document.createElement('aside');
    this.root.className = 'comments-panel';
    this.root.innerHTML = `
      <div class="comments-head">
        <span class="comments-title">Komentáře</span>
        <div class="comments-head-actions">
          <button class="comments-menu-btn" title="Další akce">⋯</button>
          <div class="comments-menu" hidden>
            <button class="comments-menu-item" data-action="delete-resolved">Smazat vyřešené</button>
            <button class="comments-menu-item is-danger" data-action="delete-all">Smazat všechny</button>
          </div>
          <button class="comments-close" title="Zavřít panel">×</button>
        </div>
      </div>
      <div class="comments-list"></div>`;
    container.appendChild(this.root);

    this.listEl = this.root.querySelector('.comments-list')!;
    this.root.querySelector('.comments-close')!.addEventListener('click', () => this.hide());
    this.setupMenu();

    this.unsub = store.subscribe(() => this.render());

    // Když dokument komentáře má (např. otevřený z Wordu), panel otevřeme —
    // jinak by uživatel netušil, že tam jsou.
    let hadComments = false;
    store.subscribe((s) => {
      const has = !!(s.doc.comments && s.doc.comments.length);
      if (has && !hadComments) this.show();
      hadComments = has;
    });

    // Změna dokumentu mění pozice marků → mění pořadí vláken.
    editor.on('update', () => this.render());

    // Zvýraznění vlákna podle pozice kurzoru.
    editor.on('selectionUpdate', () => {
      const ids = commentIdsAtSelection(editor);
      const changed = ids.join(',') !== this.activeIds.join(',');
      this.activeIds = ids;
      if (changed) this.render();
    });

    this.render();
  }

  /** Menu hromadných akcí v hlavičce panelu. */
  private setupMenu(): void {
    const btn = this.root.querySelector('.comments-menu-btn') as HTMLButtonElement;
    const menu = this.root.querySelector('.comments-menu') as HTMLElement;

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      // Položky, které nedávají smysl, zakážeme (místo skrývání — je vidět, že existují).
      const resolvedItem = menu.querySelector('[data-action="delete-resolved"]') as HTMLButtonElement;
      const allItem = menu.querySelector('[data-action="delete-all"]') as HTMLButtonElement;
      const resolved = resolvedThreadCount();
      const total = threadCount();
      resolvedItem.disabled = resolved === 0;
      resolvedItem.textContent = resolved ? `Smazat vyřešené (${resolved})` : 'Smazat vyřešené';
      allItem.disabled = total === 0;
      allItem.textContent = total ? `Smazat všechny (${total})` : 'Smazat všechny';
      menu.hidden = !menu.hidden;
    });

    // Zavření kliknutím mimo.
    document.addEventListener('click', (e) => {
      if (!menu.hidden && !menu.contains(e.target as HTMLElement) && e.target !== btn) {
        menu.hidden = true;
      }
    });

    menu.addEventListener('click', (e) => {
      const item = (e.target as HTMLElement).closest('.comments-menu-item') as HTMLButtonElement;
      if (!item || item.disabled) return;
      menu.hidden = true;

      if (item.dataset.action === 'delete-resolved') {
        const n = resolvedThreadCount();
        if (!n) return;
        if (!confirm(`Smazat ${n} ${pluralThreads(n)}?\n\nSmažou se všechna vyřešená vlákna včetně odpovědí. Akci nelze vrátit.`)) return;
        deleteResolvedComments(this.editor);
        this.render();
      }

      if (item.dataset.action === 'delete-all') {
        const n = threadCount();
        if (!n) return;
        if (!confirm(`Smazat všech ${n} ${pluralThreads(n)}?\n\nSmažou se všechny komentáře v dokumentu včetně odpovědí. Akci nelze vrátit.`)) return;
        deleteAllComments(this.editor);
        this.render();
      }
    });
  }

  show(): void {
    this.open = true;
    this.root.classList.add('is-open');
    this.onVisibilityChange?.(true);
  }

  hide(): void {
    this.open = false;
    this.root.classList.remove('is-open');
    this.onVisibilityChange?.(false);
  }

  toggle(): void {
    this.open ? this.hide() : this.show();
  }

  /** Vytvoří komentář k aktuálnímu výběru a otevře panel s editací. */
  addForSelection(): void {
    const { from, to } = this.editor.state.selection;
    if (from === to) {
      alert('Nejdřív vyberte text, který chcete okomentovat.');
      return;
    }
    this.show();
    this.renderNewCommentForm();
  }

  private renderNewCommentForm(): void {
    // Dočasná bublina pro nový komentář na začátku seznamu.
    const box = document.createElement('div');
    box.className = 'comment-thread is-new';
    box.innerHTML = `
      <textarea class="comment-input" placeholder="Napište komentář…" rows="3"></textarea>
      <div class="comment-actions">
        <button class="comment-btn comment-btn-primary">Přidat</button>
        <button class="comment-btn comment-btn-cancel">Zrušit</button>
      </div>`;
    this.listEl.prepend(box);

    const ta = box.querySelector('textarea') as HTMLTextAreaElement;
    ta.focus();

    const submit = () => {
      const created = addComment(this.editor, ta.value);
      if (created) this.render();
      else box.remove();
    };
    box.querySelector('.comment-btn-primary')!.addEventListener('click', submit);
    box.querySelector('.comment-btn-cancel')!.addEventListener('click', () => box.remove());
    ta.addEventListener('keydown', (e) => {
      // Ctrl+Enter odešle (Word-like), Esc zruší.
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); submit(); }
      if (e.key === 'Escape') { e.preventDefault(); box.remove(); }
    });
  }

  private render(): void {
    const roots = rootComments(this.editor);
    this.listEl.innerHTML = '';

    if (!roots.length) {
      const empty = document.createElement('div');
      empty.className = 'comments-empty';
      empty.textContent = 'Zatím žádné komentáře. Vyberte text a klikněte na Nový komentář.';
      this.listEl.appendChild(empty);
      return;
    }

    for (const root of roots) {
      this.listEl.appendChild(this.renderThread(root));
    }
  }

  private renderThread(root: DocComment): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'comment-thread';
    if (root.resolved) wrap.classList.add('is-resolved');
    if (this.activeIds.includes(root.id)) wrap.classList.add('is-active');

    wrap.appendChild(this.renderBubble(root, true));
    for (const reply of repliesOf(root.id)) {
      wrap.appendChild(this.renderBubble(reply, false));
    }

    // Pole pro odpověď
    const replyBox = document.createElement('div');
    replyBox.className = 'comment-reply';
    replyBox.innerHTML = `
      <input class="comment-reply-input" placeholder="Odpovědět…" />`;
    const input = replyBox.querySelector('input') as HTMLInputElement;
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (replyToComment(root.id, input.value, this.editor)) {
          input.value = '';
          this.render();
        }
      }
    });
    wrap.appendChild(replyBox);

    // Kliknutí na vlákno → skoč na komentovaný text
    wrap.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('button, input, textarea')) return;
      this.scrollToComment(root.id);
    });

    return wrap;
  }

  private renderBubble(c: DocComment, isRoot: boolean): HTMLElement {
    const el = document.createElement('div');
    el.className = isRoot ? 'comment-bubble is-root' : 'comment-bubble is-reply';

    const when = formatDate(c.date);
    el.innerHTML = `
      <div class="comment-meta">
        <span class="comment-avatar" style="background:${authorColor(c.author)}">${escapeHtml(c.initials)}</span>
        <span class="comment-author">${escapeHtml(c.author)}</span>
        <span class="comment-date">${escapeHtml(when)}</span>
      </div>
      <div class="comment-text">${escapeHtml(c.text)}</div>`;

    const actions = document.createElement('div');
    actions.className = 'comment-actions';

    if (isRoot) {
      const resolveBtn = document.createElement('button');
      resolveBtn.className = 'comment-btn';
      resolveBtn.textContent = c.resolved ? 'Znovu otevřít' : 'Vyřešit';
      resolveBtn.addEventListener('click', () => {
        toggleResolved(c.id, this.editor);
        this.render();
      });
      actions.appendChild(resolveBtn);
    }

    const delBtn = document.createElement('button');
    delBtn.className = 'comment-btn comment-btn-danger';
    delBtn.textContent = 'Smazat';
    delBtn.addEventListener('click', () => {
      deleteComment(this.editor, c.id);
      this.render();
    });
    actions.appendChild(delBtn);

    el.appendChild(actions);
    return el;
  }

  /** Najde v dokumentu text daného komentáře a odscrolluje na něj. */
  private scrollToComment(commentId: number): void {
    const el = this.editor.view.dom.querySelector(`[data-comment-id="${commentId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (el as HTMLElement).classList.add('is-flash');
      setTimeout(() => (el as HTMLElement).classList.remove('is-flash'), 900);
    }
  }

  destroy(): void {
    this.unsub();
    this.root.remove();
  }
}

/**
 * Barva avataru podle autora — Word dává každému autorovi vlastní barvu
 * z pevné palety. Stejné jméno tedy vždy dostane stejnou barvu.
 */
/** České skloňování slova „vlákno" podle počtu. */
function pluralThreads(n: number): string {
  if (n === 1) return 'vlákno';
  if (n >= 2 && n <= 4) return 'vlákna';
  return 'vláken';
}

function authorColor(author: string): string {
  const palette = [
    '#2b579a', // modrá (Word primární)
    '#c43e1c', // cihlová
    '#107c10', // zelená
    '#8764b8', // fialová
    '#c19c00', // zlatá
    '#008272', // tyrkysová
    '#a4262c', // červená
  ];
  let hash = 0;
  for (let i = 0; i < author.length; i++) {
    hash = (hash * 31 + author.charCodeAt(i)) >>> 0;
  }
  return palette[hash % palette.length];
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: '2-digit' });
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
